/**
 * The HTTP ingest contract: the batch, the event, the identify call.
 *
 * Source of truth: build plan section 5.9.2 (request body, with the real
 * example), 5.9.3 (limits) and 5.9.6 (the other ingest endpoints).
 *
 * These schemas describe what a conforming client sends. They are strict on
 * purpose: the SDK validates against them before a byte leaves the browser, and
 * the contract round trip test asserts the collector accepts everything they
 * accept. The collector itself does not reject on an event level violation, it
 * degrades, which is what `normalizeEvent` in ./normalize.ts implements.
 *
 * Every TypeScript type in this module is inferred from its schema. None is
 * written by hand, so none can drift, with one unavoidable exception:
 * `PropertyValue` is recursive and TypeScript cannot infer a recursive type
 * through `z.lazy`, so the type is declared and the schema is annotated with
 * it. The compiler still checks the two against each other.
 */
import { z } from 'zod';
import { MAX_ANON_ID_CHARS, MAX_DISTINCT_ID_CHARS, MAX_EVENT_NAME_CHARS, MAX_EVENTS_PER_BATCH, MAX_PROPERTY_COUNT, MAX_PROPERTY_DEPTH, MAX_PROPERTY_KEY_CHARS, MAX_SDK_NAME_CHARS, MAX_SDK_VERSION_CHARS, MAX_STRING_VALUE_CHARS, MIN_EVENT_NAME_CHARS, MIN_EVENTS_PER_BATCH, } from './limits.js';
import { WRITE_KEY_RE, codepointLength, hasControlCodepoint } from './keys.js';
import { PLACEHOLDER_IDS } from './spec.js';
/* ------------------------------------------------------------------------- *
 * Primitive shapes.
 * ------------------------------------------------------------------------- */
/**
 * A ULID in Crockford base32. Accepted case insensitively and normalised to
 * upper case by {@link normalizeUlid}: rejecting a lower case ULID would drop
 * an event over a cosmetic difference, and the value is an idempotency key, so
 * losing it is worse than normalising it.
 */
export const ULID_RE = /^[0-7][0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{25}$/;
/** A UUID in canonical form, any version. */
export const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/**
 * ISO-8601 with an explicit UTC designator or a numeric offset. A naive
 * timestamp is rejected: an event whose timezone we have to guess is an event
 * whose bucket we get wrong.
 */
export const ISO_8601_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;
/** Upper case form of a ULID, or null when the input is not one. */
export function normalizeUlid(value) {
    return ULID_RE.test(value) ? value.toUpperCase() : null;
}
/**
 * True when `value` is a syntactically valid, calendar valid ISO-8601 instant.
 *
 * The component checks are not redundant. `Date.parse` in V8 silently rolls
 * `2026-02-30` over into March, while Python's `datetime.fromisoformat`
 * rejects it, and a contract whose two implementations disagree about which
 * dates exist is a contract that loses events on one side only.
 */
export function isIso8601(value) {
    if (!ISO_8601_RE.test(value))
        return false;
    if (!Number.isFinite(Date.parse(value)))
        return false;
    const year = Number.parseInt(value.slice(0, 4), 10);
    const month = Number.parseInt(value.slice(5, 7), 10);
    const day = Number.parseInt(value.slice(8, 10), 10);
    const hour = Number.parseInt(value.slice(11, 13), 10);
    const minute = Number.parseInt(value.slice(14, 16), 10);
    const second = Number.parseInt(value.slice(17, 19), 10);
    if (month < 1 || month > 12 || day < 1)
        return false;
    if (hour > 23 || minute > 59 || second > 59)
        return false;
    // setUTCFullYear rather than Date.UTC, because Date.UTC maps a two digit
    // year into the 1900s and our regex permits a year such as 0026.
    const probe = new Date(0);
    probe.setUTCFullYear(year, month - 1, day);
    return (probe.getUTCFullYear() === year &&
        probe.getUTCMonth() === month - 1 &&
        probe.getUTCDate() === day);
}
const Iso8601Schema = z
    .string()
    .refine(isIso8601, { message: 'expected an ISO-8601 instant with a timezone' });
const UlidSchema = z
    .string()
    .refine((value) => ULID_RE.test(value), { message: 'expected a ULID' });
/**
 * A customer supplied identifier that ends up inside a DynamoDB sort key. It
 * must be non empty, bounded, and free of codepoints below 0x20 so the internal
 * sentinel namespace stays unreachable.
 */
function sortKeySafeString(minChars, maxChars, label) {
    return z
        .string()
        .refine((value) => codepointLength(value) >= minChars, {
        message: `${label} must be at least ${minChars} characters`,
    })
        .refine((value) => codepointLength(value) <= maxChars, {
        message: `${label} must be at most ${maxChars} characters`,
    })
        .refine((value) => !hasControlCodepoint(value), {
        message: `${label} must not contain a codepoint below 0x20`,
    });
}
/** A bounded string with no charset restriction, measured in codepoints. */
function boundedString(maxChars, label) {
    return z.string().refine((value) => codepointLength(value) <= maxChars, {
        message: `${label} must be at most ${maxChars} characters`,
    });
}
const PropertyScalarSchema = z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);
const PropertyArraySchema = z.array(z.union([z.string(), z.number().finite(), z.boolean()]));
export const PropertyValueSchema = z.lazy(() => z.union([
    PropertyScalarSchema,
    PropertyArraySchema,
    z.record(z.string(), PropertyValueSchema),
]));
/**
 * Container depth of a value: 0 for a scalar, and one plus the deepest child
 * for a container. The `props` object itself counts, so `{a: 1}` is 1 and
 * `{a: {b: {c: 1}}}` is 3, which is the deepest form MAX_PROPERTY_DEPTH allows.
 */
export function containerDepth(value) {
    if (Array.isArray(value)) {
        let deepest = 0;
        for (const item of value) {
            const depth = containerDepth(item);
            if (depth > deepest)
                deepest = depth;
        }
        return 1 + deepest;
    }
    if (value !== null && typeof value === 'object') {
        let deepest = 0;
        for (const item of Object.values(value)) {
            const depth = containerDepth(item);
            if (depth > deepest)
                deepest = depth;
        }
        return 1 + deepest;
    }
    return 0;
}
/** Machine readable tags on every property level issue, so a caller can act on
 *  the specific rule that failed rather than parse a message. */
export const PROPERTY_RULES = [
    'max_property_count',
    'max_property_key_chars',
    'property_key_charset',
    'max_string_value_chars',
    'max_property_depth',
];
function checkStringValueLengths(value, path, ctx) {
    if (typeof value === 'string') {
        if (codepointLength(value) > MAX_STRING_VALUE_CHARS) {
            ctx.addIssue({
                code: 'custom',
                path,
                message: `property string values must be at most ${MAX_STRING_VALUE_CHARS} characters`,
                params: { rule: 'max_string_value_chars' },
            });
        }
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            checkStringValueLengths(item, [...path, index], ctx);
        });
        return;
    }
    if (value !== null && typeof value === 'object') {
        for (const [key, item] of Object.entries(value)) {
            checkStringValueLengths(item, [...path, key], ctx);
        }
    }
}
export const PropertiesSchema = z
    .record(z.string(), PropertyValueSchema)
    .superRefine((props, ctx) => {
    const keys = Object.keys(props);
    if (keys.length > MAX_PROPERTY_COUNT) {
        ctx.addIssue({
            code: 'custom',
            message: `at most ${MAX_PROPERTY_COUNT} properties per event`,
            params: { rule: 'max_property_count' },
        });
    }
    for (const key of keys) {
        if (codepointLength(key) > MAX_PROPERTY_KEY_CHARS) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: `property keys must be at most ${MAX_PROPERTY_KEY_CHARS} characters`,
                params: { rule: 'max_property_key_chars' },
            });
        }
        if (key.length === 0 || hasControlCodepoint(key)) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: 'property keys must be non empty and free of codepoints below 0x20',
                params: { rule: 'property_key_charset' },
            });
        }
    }
    if (containerDepth(props) > MAX_PROPERTY_DEPTH) {
        ctx.addIssue({
            code: 'custom',
            message: `property nesting must be at most ${MAX_PROPERTY_DEPTH} deep`,
            params: { rule: 'max_property_depth' },
        });
    }
    checkStringValueLengths(props, [], ctx);
});
/* ------------------------------------------------------------------------- *
 * The event.
 * ------------------------------------------------------------------------- */
/**
 * One captured event.
 *
 * Unknown keys are stripped rather than rejected. That is deliberate and it is
 * how FR-ING-11 is enforced on the client side too: a client that sends
 * `project_id`, `org_id`, `env`, `received_at`, `ip_country` or `is_bot` has
 * those keys discarded here, because every one of them is server stamped.
 */
export const EventSchema = z.object({
    /** Client generated idempotency key, preserved across retries. */
    event_id: UlidSchema,
    /** MIN_EVENT_NAME_CHARS to MAX_EVENT_NAME_CHARS characters, no codepoint below 0x20. */
    name: sortKeySafeString(MIN_EVENT_NAME_CHARS, MAX_EVENT_NAME_CHARS, 'event name'),
    /** Client clock. Clamped server side per FR-ING-10, never rejected. */
    ts: Iso8601Schema,
    distinct_id: sortKeySafeString(1, MAX_DISTINCT_ID_CHARS, 'distinct_id'),
    /** Present until identify() has run. */
    anon_id: boundedString(MAX_ANON_ID_CHARS, 'anon_id')
        .refine((value) => UUID_RE.test(value), { message: 'expected a UUID' })
        .optional(),
    /** Minted by the SDK. The server never re-segments a session. */
    session_id: UlidSchema.optional(),
    /** Split server side into url_host and url_path. */
    url: z.string().optional(),
    /** Incremented by the SDK on each retry. Client asserted, therefore rate
     *  capped server side rather than trusted. */
    retry: z.number().int().min(0),
    props: PropertiesSchema.optional(),
});
/* ------------------------------------------------------------------------- *
 * The batch.
 * ------------------------------------------------------------------------- */
export const SdkSchema = z.object({
    /** SDK name, for example `zelisight-js`. */
    n: z.string().min(1).max(MAX_SDK_NAME_CHARS),
    /** SDK version, for example `1.0.3`. */
    v: z.string().min(1).max(MAX_SDK_VERSION_CHARS),
});
export const WriteKeySchema = z
    .string()
    .refine((value) => WRITE_KEY_RE.test(value), {
    message: 'expected a write key of the form zs_<p|s|d>_<26 base62 chars>',
});
/** `POST /v1/e`. */
export const BatchSchema = z.object({
    write_key: WriteKeySchema,
    /** Used only for clock skew telemetry. */
    sent_at: Iso8601Schema,
    sdk: SdkSchema,
    events: z.array(EventSchema).min(MIN_EVENTS_PER_BATCH).max(MAX_EVENTS_PER_BATCH),
});
/* ------------------------------------------------------------------------- *
 * Identify.
 * ------------------------------------------------------------------------- */
/**
 * `POST /v1/i`. Append only and irreversible: an attempt to stitch an `anon_id`
 * already bound to a different `distinct_id` returns 409 `identity_conflict`,
 * never a silent re-merge.
 */
export const IdentifySchema = z
    .object({
    write_key: WriteKeySchema,
    anon_id: boundedString(MAX_ANON_ID_CHARS, 'anon_id').refine((value) => UUID_RE.test(value), { message: 'expected a UUID' }),
    distinct_id: sortKeySafeString(1, MAX_DISTINCT_ID_CHARS, 'distinct_id'),
    properties: PropertiesSchema.optional(),
})
    // The same two rules the generated Python validator applies, from the same
    // list, so the SDK refuses a binding the collector would refuse anyway rather
    // than sending it and learning from a 400.
    .superRefine((value, ctx) => {
    if (value.distinct_id === value.anon_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['distinct_id'],
            message: 'distinct_id must identify a person, not repeat the anon_id',
        });
    }
    if (PLACEHOLDER_IDS.includes(value.distinct_id.trim().toLowerCase())) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['distinct_id'],
            message: 'distinct_id is a placeholder value, not an identifier',
        });
    }
});
/* ------------------------------------------------------------------------- *
 * Runtime config, GET /v1/config/<key_prefix>.
 * ------------------------------------------------------------------------- */
export const RuntimeConfigSchema = z.object({
    capture_enabled: z.boolean(),
    kill_switch: z.boolean(),
    replay_sample_rate: z.number().min(0).max(1),
    flush_interval_ms: z.number().int().positive(),
    /** ADR 0034. Absent from an older collector, which the SDK reads as off. */
    heatmaps: z.object({ enabled: z.boolean() }).optional(),
});
//# sourceMappingURL=event.js.map